import { use, useCallback } from 'react';
import styled from 'styled-components';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { SequencerContext } from '../sequencer.tsx';
import { PracticeVideoContext, songSources } from './practice_video_context.tsx';
import { YouTubeMark } from './youtube_mark.tsx';

// What the player plays (GitHub issue #4041): one oval of touching segments, Piano when the song has MIDI,
// Vocals when it has vocals and Video always. Shown only for a song with practice videos, where it replaces
// the Vocals button, and only when there is a choice: a song whose only sound is its videos has no oval.
export enum PlayerSource {
  Piano = 'piano',
  Vocals = 'vocals',
  Video = 'video',
}

export function useSelectPlayerSource(): (source: PlayerSource) => void {
  const { setVideoMode } = use(PracticeVideoContext);
  const [_showVocalsByDefault, setShowVocalsByDefault] = useLocalStorage(LocalStorageKey.ShowVocalsByDefault);
  return useCallback(
    (source: PlayerSource) => {
      // video mode keeps the mixer card open while it lasts (useMixerVisibility)
      setVideoMode(source === PlayerSource.Video);
      if (source !== PlayerSource.Video) {
        setShowVocalsByDefault(source === PlayerSource.Vocals);
      }
    },
    [setShowVocalsByDefault, setVideoMode],
  );
}

export function SourceSelector() {
  const { hymn, vocalsEnabled } = use(SequencerContext);
  const { hasPiano, hasVocals } = songSources(hymn);
  const { isVideoMode } = use(PracticeVideoContext);
  const selectSource = useSelectPlayerSource();
  const selected = isVideoMode ? PlayerSource.Video : vocalsEnabled ? PlayerSource.Vocals : PlayerSource.Piano;
  const sources = [
    ...(hasPiano ? [PlayerSource.Piano] : []),
    ...(hasVocals ? [PlayerSource.Vocals] : []),
    PlayerSource.Video,
  ];
  if (sources.length < 2) {
    return null;
  }

  return (
    <SourceOval role="group" aria-label="Play">
      {sources.map((source) => {
        const isSelected = source === selected;
        return (
          <SourceSegment
            key={source}
            type="button"
            aria-pressed={isSelected}
            $selected={isSelected}
            onClick={() => selectSource(source)}
          >
            {source === PlayerSource.Piano ? (
              <MusicNoteIcon />
            ) : source === PlayerSource.Vocals ? (
              <MicrophoneIcon />
            ) : (
              <YouTubeMark width={15} height={11} />
            )}
            {sourceLabels[source]}
          </SourceSegment>
        );
      })}
    </SourceOval>
  );
}

const sourceLabels: Record<PlayerSource, string> = {
  [PlayerSource.Piano]: 'Piano',
  [PlayerSource.Vocals]: 'Vocals',
  [PlayerSource.Video]: 'Video',
};

function MusicNoteIcon() {
  return (
    <svg
      aria-hidden="true"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

function MicrophoneIcon() {
  return (
    <svg
      aria-hidden="true"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0M12 17v5M8 22h8" />
    </svg>
  );
}

const ovalBorder = '1.5px solid var(--color-text)';

const SourceOval = styled.div`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 36px;
  margin: 6px 0;
  border: ${ovalBorder};
  border-radius: 18px;
  overflow: hidden;
  background-color: var(--color-background);
  flex-shrink: 0;
`;

const SourceSegment = styled.button<{ $selected: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  & + & {
    border-left: ${ovalBorder};
  }
  background-color: ${(props) => (props.$selected ? 'var(--color-text)' : 'var(--color-background)')};
  color: ${(props) => (props.$selected ? 'var(--color-background)' : 'var(--color-text)')};
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 13px;
  cursor: pointer;
`;
